import { useState } from "react";
import { publicTrees, publicBairros, publicGreenAreas, publicSpecies } from "@/mocks/publico";
import { PublicRiskBadge } from "./PublicBadges";

const MAP_MOCK_URL =
  "https://readdy.ai/api/search-image?query=Abstract%20stylized%20city%20map%20with%20green%20dots%20representing%20trees%2C%20green%20polygons%20for%20parks%2C%20minimalist%20cartographic%20style%20with%20soft%20earth%20tones%2C%20light%20cream%20background%2C%20modern%20GIS%20aesthetic%2C%20clean%20geometric%20lines%2C%20professional%20urban%20planning%20visualization&width=1400&height=700&seq=publico-mapa-2026&orientation=landscape";

const layers = [
  { key: "arvores", label: "Árvores inventariadas", icon: "ri-plant-line" },
  { key: "areasVerdes", label: "Áreas verdes", icon: "ri-landscape-line" },
  { key: "ucs", label: "UCs", icon: "ri-shield-star-line" },
  { key: "risco", label: "Risco agregado", icon: "ri-error-warning-line" },
  { key: "bairros", label: "Bairros", icon: "ri-building-line" },
];

export default function PublicMapSection() {
  const [activeLayers, setActiveLayers] = useState<string[]>(["arvores", "areasVerdes"]);
  const [selectedTree, setSelectedTree] = useState<typeof publicTrees[0] | null>(null);
  const [searchBairro, setSearchBairro] = useState("");
  const [searchEspecie, setSearchEspecie] = useState("");

  const toggleLayer = (key: string) => {
    setActiveLayers((prev) =>
      prev.includes(key) ? prev.filter((l) => l !== key) : [...prev, key]
    );
  };

  const filteredTrees = publicTrees.filter((t) => {
    if (searchBairro && t.bairro !== searchBairro) return false;
    if (searchEspecie && t.especie !== searchEspecie) return false;
    return true;
  });

  return (
    <section id="mapa" className="py-14 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">Mapa Ambiental Público</h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            Visualize as árvores do CTA, áreas verdes e Unidades de Conservação no mapa interativo. Nenhum dado sensível é exibido publicamente.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Controls */}
          <div className="lg:col-span-1 space-y-4">
            {/* Filter */}
            <div className="bg-background-50 rounded-xl p-4 border border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Filtros</h3>
              <select
                value={searchBairro}
                onChange={(e) => setSearchBairro(e.target.value)}
                className="w-full px-3 py-2 text-sm text-foreground-700 bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors mb-3"
              >
                <option value="">Todos os bairros</option>
                {publicBairros.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              <select
                value={searchEspecie}
                onChange={(e) => setSearchEspecie(e.target.value)}
                className="w-full px-3 py-2 text-sm text-foreground-700 bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
              >
                <option value="">Todas as espécies</option>
                {publicSpecies.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Layers */}
            <div className="bg-background-50 rounded-xl p-4 border border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Camadas</h3>
              <div className="space-y-2">
                {layers.map((layer) => (
                  <label
                    key={layer.key}
                    className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-background-100 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={activeLayers.includes(layer.key)}
                      onChange={() => toggleLayer(layer.key)}
                      className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                    />
                    <i className={`${layer.icon} text-foreground-500 text-sm w-4 h-4 flex items-center justify-center`} />
                    <span className="text-sm text-foreground-700">{layer.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Tree List */}
            <div className="bg-background-50 rounded-xl p-4 border border-background-200/70 max-h-[400px] overflow-y-auto">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">
                Árvores públicas ({filteredTrees.length})
              </h3>
              <div className="space-y-1.5">
                {filteredTrees.slice(0, 20).map((tree) => (
                  <button
                    key={tree.id}
                    onClick={() => setSelectedTree(tree)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedTree?.id === tree.id
                        ? "bg-primary-50 text-primary-700 font-medium"
                        : "text-foreground-600 hover:bg-background-100"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <i className="ri-plant-line w-4 h-4 flex items-center justify-center" />
                      <span>{tree.id} — {tree.especie}</span>
                    </div>
                    <p className="text-xs text-foreground-400 mt-0.5 ml-6">{tree.bairro}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Map Area */}
          <div className="lg:col-span-3">
            {/* Mock Map */}
            <div className="relative rounded-xl overflow-hidden border border-background-200/70 h-[500px] md:h-[600px] bg-background-50">
              <img
                src={MAP_MOCK_URL}
                alt="Mapa Ambiental"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-foreground-600 border border-background-200">
                <strong>{filteredTrees.length}</strong> árvores visíveis
              </div>

              {/* Selected Tree Popup */}
              {selectedTree && (
                <div className="absolute bottom-4 left-4 right-4 bg-white rounded-xl p-5 border border-background-200 shadow-sm max-w-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">
                        {selectedTree.id} — {selectedTree.especie}
                      </h4>
                      <p className="text-xs text-foreground-500 italic mt-0.5">
                        {selectedTree.nomeCientifico}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs text-foreground-600">
                          <i className="ri-map-pin-line w-3 h-3 inline-flex items-center justify-center mr-1" />
                          {selectedTree.bairro}
                        </span>
                        <span className="text-xs text-foreground-600">
                          <i className="ri-calendar-line w-3 h-3 inline-flex items-center justify-center mr-1" />
                          {selectedTree.ultimaVistoria}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <PublicRiskBadge risco={selectedTree.risco} />
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                          selectedTree.estado === "Bom" ? "bg-primary-50 text-primary-700" :
                          selectedTree.estado === "Regular" ? "bg-warning-50 text-warning-700" :
                          "bg-danger-50 text-danger-700"
                        }`}>
                          {selectedTree.estado}
                        </span>
                      </div>
                      {selectedTree.recomendacao && (
                        <p className="text-xs text-foreground-500 mt-2 italic">
                          {selectedTree.recomendacao}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedTree(null)}
                      className="p-1 rounded-md hover:bg-background-100 text-foreground-400 cursor-pointer"
                    >
                      <i className="ri-close-line w-4 h-4 flex items-center justify-center" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Map Legend */}
            <div className="flex flex-wrap gap-4 mt-4 justify-center">
              <div className="flex items-center gap-1.5 text-xs text-foreground-500">
                <span className="w-3 h-3 rounded-full bg-primary-500" />
                Árvores
              </div>
              <div className="flex items-center gap-1.5 text-xs text-foreground-500">
                <span className="w-3 h-3 rounded-sm bg-accent-500/40" />
                Áreas Verdes
              </div>
              <div className="flex items-center gap-1.5 text-xs text-foreground-500">
                <span className="w-3 h-3 rounded-sm bg-warning-500/30" />
                UCs
              </div>
              <div className="flex items-center gap-1.5 text-xs text-foreground-500">
                <span className="w-3 h-3 rounded-full bg-danger-500" />
                Risco Crítico
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}